import { InjectionToken } from '@angular/core';
import type { User } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';
import type { LoginResponse } from '../login';
import type { RegisterUser } from '../register';

/**
 * Contract that every authentication strategy must fulfil.
 *
 * Implement this interface in a brand-specific service and provide it via
 * `{ provide: AUTH_STRATEGY, useClass: MyAuthApiClient }` in the brand's
 * `app.config.ts`.
 *
 * The default implementation is `AuthApiClient` from `@indutrust/auth-data`,
 * wired via `{ provide: AUTH_STRATEGY, useExisting: AuthApiClient }`.
 */
export type AuthStrategy = {
  /**
   * Authenticates a user.
   * @param email - The user's email address.
   * @param password - The user's password.
   */
  login: (email: string, password: string) => Observable<LoginResponse>;

  /**
   * Registers a new user account.
   * @param user - The registration payload.
   * @param accessToken - Optional invitation Bearer token.
   */
  register: (
    user: RegisterUser,
    accessToken?: string
  ) => Observable<LoginResponse>;

  /**
   * Triggers a forgot-password email.
   * @param email - The email address to send the reset link to.
   */
  forgotPassword: (email: string) => Observable<unknown>;

  /**
   * Completes a password reset flow.
   * @param password - The new password.
   * @param token - The reset token from the email link.
   */
  resetPassword: (password: string, token: string) => Observable<LoginResponse>;

  /**
   * Fetches the currently authenticated user from the backend.
   */
  getConnectedUser: () => Observable<User>;
};

/**
 * Injection token for the active authentication strategy.
 *
 * Provide this in each app's `app.config.ts`:
 * @example
 * // Default (email/password):
 * { provide: AUTH_STRATEGY, useExisting: AuthApiClient }
 *
 * // OIDC brand:
 * { provide: AUTH_STRATEGY, useClass: OidcAuthApiClient }
 */
export const AUTH_STRATEGY = new InjectionToken<AuthStrategy>('AUTH_STRATEGY');
